import styled from '@emotion/styled'
import { getTime, subMonths, subYears } from 'date-fns'
import { FC, useEffect, useState } from 'react'

import {
  Alert,
  Chart,
  DurationButtons,
  LoadingIndicator,
  Space,
  Typography
} from '@/components/core-v2'
import { TODAY } from '@/const'

const StyledChartTitle = styled.div`
  padding-bottom: 2px;
  border-bottom: 1px solid var(--White90);
`

const StyledPerformanceChart = styled.div`
  min-height: 260px;
  border-bottom: solid 4px var(--White98);
`

export type PerformanceChartProps = {
  isError: boolean
  isLoading: boolean
  metrics: Record<string, number>
  title: string
}

const ranges = {
  '1m': (date: Date) => subMonths(date, 1),
  '3m': (date: Date) => subMonths(date, 3),
  '6m': (date: Date) => subMonths(date, 6),
  '1y': (date: Date) => subYears(date, 1),
  all: (date: Date) => subYears(date, 100)
}

function filterData(metrics, range) {
  const startDateFn = ranges[range]
  const startDate = startDateFn(TODAY)

  const startTimestamp = getTime(startDate)

  // this needs to be improved
  const output = {}
  for (const date of Object.keys(metrics)) {
    if (Date.parse(date) >= startTimestamp) {
      output[date] = metrics[date]
    }
  }

  return output
}

export const PerformanceChart: FC<PerformanceChartProps> = ({
  isError,
  isLoading,
  metrics: rawMetrics,
  title
}) => {
  const [metrics, setMetrics] = useState({})

  useEffect(() => {
    if (rawMetrics) {
      setMetrics(rawMetrics)
    }
  }, [rawMetrics])

  const [selectedItem, setSelectedItem] = useState('all')

  if (isError) {
    return (
      <StyledPerformanceChart>
        <Space px={5} py={6}>
          <StyledChartTitle>
            <Space mb={5}>
              <Typography fontSize="13px" letterSpacing="0" variant="b3">
                {title}
              </Typography>
            </Space>
          </StyledChartTitle>
          <Space mt={5}>
            <Alert title="Error loading historical performance" type="error">
              Contact us if you keep getting this error
            </Alert>
          </Space>
        </Space>
      </StyledPerformanceChart>
    )
  }

  // A completed fetch that returns no data points (rawMetrics defined but empty) means the
  // trade's instruments have no price history to chart yet — e.g. a leverage loan that has
  // not been priced (bug 9353). Show an informative empty state instead of a blank chart.
  // `undefined` (no fetch yet) intentionally falls through to the loading/chart branches.
  const hasLoadedWithNoData = !isLoading && !!rawMetrics && Object.keys(rawMetrics).length === 0

  if (hasLoadedWithNoData) {
    return (
      <StyledPerformanceChart>
        <Space px={5} py={6}>
          <StyledChartTitle>
            <Space mb={5}>
              <Typography fontSize="13px" letterSpacing="0" variant="b3">
                {title}
              </Typography>
            </Space>
          </StyledChartTitle>
          <Space mt={5}>
            <Alert title="No historical performance available" type="information">
              There is no price history available for the underlying instruments yet, so this chart
              can&apos;t be displayed.
            </Alert>
          </Space>
        </Space>
      </StyledPerformanceChart>
    )
  }

  if (!metrics) {
    return (
      <StyledPerformanceChart>
        <Space px={5} py={6}>
          <StyledChartTitle>
            <Space mb={5}>
              <Typography fontSize="13px" letterSpacing="0" variant="b3">
                {title}
              </Typography>
            </Space>
          </StyledChartTitle>
          <Space mt={5}>
            <LoadingIndicator isLoading={isLoading} />
          </Space>
        </Space>
      </StyledPerformanceChart>
    )
  }

  return (
    <StyledPerformanceChart>
      <Space px={5} py={6}>
        <StyledChartTitle>
          <Space mb={5}>
            <Typography fontSize="13px" letterSpacing="0" variant="b3">
              {title}
            </Typography>
          </Space>
        </StyledChartTitle>
        <Space mt={5}>
          <LoadingIndicator isLoading={isLoading}>
            <>
              <DurationButtons
                items={Object.keys(ranges)}
                selectedItem={selectedItem}
                onSelect={range => {
                  setMetrics(filterData(rawMetrics, range))
                  setSelectedItem(range)
                }}
              />
              <Chart metrics={[metrics]} />
            </>
          </LoadingIndicator>
        </Space>
      </Space>
    </StyledPerformanceChart>
  )
}
